import api from './api';

export interface Ubicacion {
    nroUbicacion: number;
    nombreUbicacion: string;
    nroSuc: number;
    credito?: number;
    nroCentroCosto?: number;
    enConsignacion?: number;
    fechaVencim?: Date;
    tipoUbicacion?: string;
    direccion?: string;
    sincronizar?: number;
    telefono?: string;
    responsable?: string;
}

type UbicacionApiPayload = {
    nroUbicacion: number;
    nombreUbicacion: string;
    nroSuc?: number;
    credito?: number;
    nroCentroCosto?: number;
    enConsignacion?: number;
    fechaVencim?: Date;
    tipoUbicacion?: string;
    direccion?: string;
    sincronizar?: number;
};

export interface UbicacionSearchParams {
    searchTerm?: string;
    pageNumber?: number;
    pageSize?: number;
}

export interface UbicacionPagedResult {
    items: Ubicacion[];
    totalCount: number;
    pageNumber: number;
    pageSize: number;
    totalPages: number;
}

const ubicacionService = {
    toApiPayload: (ubicacion: Ubicacion, forcedNroUbicacion?: number, includeNroSucWhenZero?: boolean): UbicacionApiPayload => {
        const payload: UbicacionApiPayload = {
            nroUbicacion: forcedNroUbicacion ?? ubicacion.nroUbicacion,
            nombreUbicacion: ubicacion.nombreUbicacion
        };

        if (ubicacion.nroSuc > 0 || includeNroSucWhenZero) payload.nroSuc = ubicacion.nroSuc;
        if (ubicacion.credito != null) payload.credito = ubicacion.credito;
        if (ubicacion.nroCentroCosto != null) payload.nroCentroCosto = ubicacion.nroCentroCosto;
        if (ubicacion.enConsignacion != null) payload.enConsignacion = ubicacion.enConsignacion;
        if (ubicacion.fechaVencim != null) payload.fechaVencim = ubicacion.fechaVencim;
        if (ubicacion.tipoUbicacion != null) payload.tipoUbicacion = ubicacion.tipoUbicacion;
        if (ubicacion.direccion != null) payload.direccion = ubicacion.direccion;
        if (ubicacion.sincronizar != null) payload.sincronizar = ubicacion.sincronizar;

        return payload;
    },

    getUbicaciones: async (params?: UbicacionSearchParams): Promise<Ubicacion[]> => {
        const response = await api.get('/api/Ubicaciones', params ? { params } : {});
        const data = response.data;
        if (Array.isArray(data)) return data;
        if (data && typeof data === 'object' && 'items' in data) {
            return data.items as Ubicacion[];
        }
        return [];
    },

    getUbicacionesPaged: async (params: UbicacionSearchParams): Promise<UbicacionPagedResult> => {
        const response = await api.get('/api/Ubicaciones', { params });
        const data = response.data;
        const pageNumber = params.pageNumber || 1;
        const pageSize = params.pageSize || 10;
        
        if (Array.isArray(data)) {
            return {
                items: data,
                totalCount: data.length,
                pageNumber: pageNumber,
                pageSize: pageSize,
                totalPages: Math.ceil(data.length / pageSize)
            };
        }
        
        if (data && typeof data === 'object' && 'items' in data) {
            return data as UbicacionPagedResult;
        }
        
        return {
            items: [],
            totalCount: 0,
            pageNumber: pageNumber,
            pageSize: pageSize,
            totalPages: 0
        };
    },

    getUbicacion: async (nroUbicacion: number): Promise<Ubicacion> => {
        const response = await api.get(`/api/Ubicaciones/${nroUbicacion}`);
        return response.data;
    },

    createUbicacion: async (ubicacion: Ubicacion): Promise<Ubicacion> => {
        const response = await api.post<Ubicacion>(
            '/api/Ubicaciones',
            ubicacionService.toApiPayload(ubicacion, undefined, true)
        );
        return response.data;
    },

    updateUbicacion: async (nroUbicacion: number, ubicacion: Ubicacion): Promise<void> => {
        await api.put(`/api/Ubicaciones/${nroUbicacion}`, ubicacionService.toApiPayload(ubicacion, nroUbicacion, true));
    },

    deleteUbicacion: async (nroUbicacion: number): Promise<void> => {
        await api.delete(`/api/Ubicaciones/${nroUbicacion}`);
    }
};

export default ubicacionService; 